import {action, computed, observable} from 'mobx';
import DocumentFolder from 'main/stores/document_folders/DocumentFolder';
import {successAlert} from 'shared/tools';
import {t, api} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {DomainStore, fetchModels} from 'shared/store';
import _ from 'lodash';

class DocumentFoldersState {
  store = new DomainStore();
  agent;

  @observable editingFolder = {};
  @observable errors = {};
  @observable editDocumentFolderModalOpen = false;
  @observable editMessage = 'company_settings.document_folders.Edit Document Folder';
  @observable folders = [];
  @observable reorderingFolders = [];
  @observable reorderParentFolder = null;
  @observable interactiveContextState = {};

  @action setInteractiveAgent(agent) {
    this.agent = agent;
  }

  @action updateInteractiveContextState(interactiveContextState) {
    this.interactiveContextState = interactiveContextState;
  }

  @computed get canViewNestedFolders() {
    const search = this.interactiveContextState.filter?.get('search') || '';

    return _.isEmpty(search);
  }

  @action async load() {
    await this.store._compose([
      endpoints.FOLDERS.ALL
    ]);

    this.folders = this.store._getAll(types.FOLDER, DocumentFolder);
  }

  @action async fetchSubfolders(folder) {
    if (folder.subfolders && folder.subfolders.length > 0) return;

    const store = new DomainStore();
    await store._compose([endpoints.FOLDERS.withParent(folder.id)]);
    folder.subfolders = store._getAll(types.FOLDER, DocumentFolder);
  }

  @action async deleteDocumentFolder(folder) {
    await this.store.destroy(folder);
    successAlert(t('company_settings.document_folders.alerts.Folder deleted successfully.'));
    this.agent.refresh();
  }

  @action addDocumentFolder() {
    this.editMessage = 'company_settings.document_folders.Add Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder();
  }

  @action addSubfolder(parentFolder) {
    this.editMessage = 'company_settings.document_folders.Add Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder({parentId: parentFolder.id});
  }

  @action editDocumentFolder(folder) {
    this.editMessage = 'company_settings.document_folders.Edit Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder(folder);
  }

  @action closeEditDocumentFolderModal() {
    this.editDocumentFolderModalOpen = false;
  }

  @action async openReorderFoldersModal(parentFolder = null) {
    this.reorderParentFolder = parentFolder;
    await this.fetchReorderingSubfolders(parentFolder);
  }

  @action async selectReorderParentFolder(parentFolder) {
    this.reorderParentFolder = parentFolder;
    await this.fetchReorderingSubfolders(parentFolder);
  }

  @action async fetchReorderingSubfolders(parentFolder) {
    this.reorderingFolders = _.map(await fetchModels(
      endpoints.FOLDERS.withParent(parentFolder && parentFolder.id),
      types.FOLDER
    ), folder => new DocumentFolder(folder));
  }

  @action async saveFolderOrders() {
    const folderParams = {};
    this.reorderingFolders.forEach(folder => folderParams[folder['id']] = folder['order']);

    await api.patch(endpoints.FOLDERS.REORDER, {'folders': folderParams});

    this.closeReorderFoldersModal();
    this.agent.refresh();
  }

  @action async saveDocumentFolder() {
    const {model, errors} = this.editingFolder.isNew ?
      await this.store.post(endpoints.FOLDERS.ALL, types.FOLDER, this.editingFolder) :
      await this.store.patch(this.editingFolder);

    this.errors = errors;

    if (model) {
      this.agent.refresh();
      await this.load();
      this.closeEditDocumentFolderModal();
      successAlert(t('company_settings.document_folders.alerts.Folder information saved.'));
    }
  }

  customLinksFor(folder) {
    const links = [
      {
        order: 0,
        text: 'company_settings.document_folders.Add Subfolder',
        action: folder => this.addSubfolder(folder)
      }
    ];

    if (folder.hasSubfolders) {
      links.push({
        order: 1,
        text: 'company_settings.document_folders.Reorder Subfolders',
        action: folder => this.openReorderFoldersModal(folder)
      });
    }

    return links;
  }

  @computed get parentFolderOptions() {
    const editingId = this.editingFolder.id;
    if (!editingId) return this.folders;

    return this.folders.filter(folder => folder.id !== editingId);
  }

  @computed get sortedFolders() {
    return _.orderBy(this.folders, 'order');
  }
}

export default DocumentFoldersState;
